import React, { useState } from 'react';
import Popover from '@material-ui/core/Popover';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import classNames from 'classnames';
import { lookupsJson } from '@ourbranch/lookups';

import { Label } from 'core/components/label';
import { Select } from 'core/components/select';
import Switch from 'core/components/switch';
import { Tag } from 'core/components/tag';
import { TextField } from 'core/components/text-field';
import { useViewAs } from './view-as-context';

import useStyles from './view-as-control.styles';

const VIEWS = { BRANCH: 'Branch view', AGENCY: 'Agency view' };

// Top-bar control for the View as lens: Branch view or Agency view, plus the two ways agents differ.
const ViewAsControl = () => {
  const classes = useStyles();
  const viewAs = useViewAs();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!viewAs?.available) {
    return null;
  }

  const { active, subject, hasPageSubject, enteredSubject, principal, unlicensed } = viewAs;
  const currentView = active ? VIEWS.AGENCY : VIEWS.BRANCH;

  const viewButton = (label, onClick, disabled = false) => (
    <button
      type="button"
      className={classNames(classes.segment, { [classes.segmentSelected]: currentView === label })}
      aria-pressed={currentView === label}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className={classes.control}>
      <Tag variant="internal" tooltip="Only Service reps on the Branch team see this">
        Internal
      </Tag>
      <button
        type="button"
        className={classes.trigger}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <span className={classes.triggerLabel}>View as</span>
        {currentView}
        <ArrowDropDownIcon fontSize="small" />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.menu }}
      >
        <div role="dialog" aria-label="View as">
          <div className={classes.segments} role="group" aria-label="View as">
            {viewButton(VIEWS.BRANCH, viewAs.showBranchView)}
            {viewButton(VIEWS.AGENCY, viewAs.showAgencyView, !subject)}
          </div>
          {!hasPageSubject && (
            <div className={classes.subjectFields}>
              <Label type="darkestSmallest" className={classes.hint}>
                Open a customer to use their policy's agency, or enter an affinity code and state.
              </Label>
              <TextField
                id="view-as-affinity"
                label="Affinity code"
                mode="light"
                value={enteredSubject.affinity}
                onChange={(event) =>
                  viewAs.setEnteredSubject({ ...enteredSubject, affinity: event.target.value, agencyName: null })
                }
              />
              <Select
                id="view-as-state"
                name="state"
                label="State"
                mode="light"
                options={lookupsJson.usStates}
                value={enteredSubject.state}
                onChange={(_, state) => viewAs.setEnteredSubject({ ...enteredSubject, state })}
              />
            </div>
          )}
          <Switch
            id="view-as-principal"
            label="Agency principal"
            value={principal}
            onChange={(event) => viewAs.setPrincipal(event.target.checked)}
          />
          <Switch
            id="view-as-unlicensed"
            label="Unlicensed agent"
            value={unlicensed}
            onChange={(event) => viewAs.setUnlicensed(event.target.checked)}
          />
        </div>
      </Popover>
    </div>
  );
};

export default ViewAsControl;
